import { Injectable, inject, signal, NgZone, OnDestroy } from '@angular/core';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
} from '@angular/fire/firestore';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { DEFAULT_SCHEDULE, ScheduleDefaults, TimeWindow } from '../models/schedule.model';
import { normalizeTime } from '../utils/time.util';

const SETTINGS_PATH = 'scheduleSettings/default';

@Injectable({ providedIn: 'root' })
export class ScheduleSettingsService implements OnDestroy {
  private readonly firestore = inject(Firestore);
  private readonly zone = inject(NgZone);
  private readonly ref = doc(this.firestore, SETTINGS_PATH);
  private unsubscribe: Unsubscribe | null = null;

  private readonly defaultsSignal = signal<ScheduleDefaults>(cloneDefaults(DEFAULT_SCHEDULE));
  /** Live weekly defaults (max appts/day, buffers, windows). */
  readonly defaults = this.defaultsSignal.asReadonly();
  private readonly defaultsObs = toObservable(this.defaultsSignal);

  constructor() {
    this.startListening();
  }

  ngOnDestroy(): void {
    this.unsubscribe?.();
  }

  /** RxJS view for older callers — shares the same signal. */
  defaults$(): Observable<ScheduleDefaults> {
    return this.defaultsObs;
  }

  async saveDefaults(defaults: ScheduleDefaults, ownerUid?: string | null): Promise<void> {
    const payload = this.normalizeDefaults(defaults);
    // Optimistic local update so calendar/booking UIs move immediately.
    this.defaultsSignal.set(cloneDefaults(payload));

    await setDoc(
      this.ref,
      {
        ...payload,
        ...(ownerUid ? { ownerUid } : {}),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }

  async publishOwnerUid(ownerUid: string): Promise<void> {
    await setDoc(
      this.ref,
      {
        ownerUid,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }

  async getOwnerUid(): Promise<string | null> {
    const snap = await getDoc(this.ref);
    if (!snap.exists()) return null;
    const uid = snap.data()?.['ownerUid'];
    return typeof uid === 'string' && uid ? uid : null;
  }

  private startListening(): void {
    this.unsubscribe?.();
    this.unsubscribe = onSnapshot(
      this.ref,
      (snap) => {
        const raw = snap.data() as Record<string, unknown> | undefined;
        this.zone.run(() => this.defaultsSignal.set(this.mapDefaults(raw)));
      },
      (err) => console.error('scheduleSettings onSnapshot failed', err),
    );

    void getDoc(this.ref)
      .then((snap) => {
        if (!snap.exists()) return;
        this.zone.run(() =>
          this.defaultsSignal.set(this.mapDefaults(snap.data() as Record<string, unknown>)),
        );
      })
      .catch((err) => console.error('scheduleSettings getDoc failed', err));
  }

  private normalizeDefaults(defaults: ScheduleDefaults): ScheduleDefaults {
    return {
      weekdayWindows: this.cleanWindows(defaults.weekdayWindows),
      weekendWindows: this.cleanWindows(defaults.weekendWindows),
      bufferMinutes: Math.max(0, Math.round(Number(defaults.bufferMinutes) || 0)),
      startIntervalMinutes: Math.max(5, Math.round(Number(defaults.startIntervalMinutes) || 15)),
      maxAppointmentsPerDay: Math.max(1, Math.round(Number(defaults.maxAppointmentsPerDay) || 1)),
    };
  }

  private cleanWindows(windows: TimeWindow[]): TimeWindow[] {
    return windows
      .map((w) => ({
        startTime: normalizeTime(w.startTime),
        endTime: normalizeTime(w.endTime),
      }))
      .filter((w) => w.startTime < w.endTime);
  }

  private mapDefaults(raw: Record<string, unknown> | undefined): ScheduleDefaults {
    if (!raw) return cloneDefaults(DEFAULT_SCHEDULE);
    const weekdayWindows = Array.isArray(raw['weekdayWindows'])
      ? (raw['weekdayWindows'] as TimeWindow[])
      : DEFAULT_SCHEDULE.weekdayWindows;
    const weekendWindows = Array.isArray(raw['weekendWindows'])
      ? (raw['weekendWindows'] as TimeWindow[])
      : DEFAULT_SCHEDULE.weekendWindows;
    const cleanedWeekday = this.cleanWindows(weekdayWindows);
    const cleanedWeekend = this.cleanWindows(weekendWindows);
    return {
      weekdayWindows: cleanedWeekday.length
        ? cleanedWeekday
        : [...DEFAULT_SCHEDULE.weekdayWindows],
      weekendWindows: cleanedWeekend.length
        ? cleanedWeekend
        : [...DEFAULT_SCHEDULE.weekendWindows],
      bufferMinutes: Math.max(
        0,
        Math.round(Number(raw['bufferMinutes'] ?? DEFAULT_SCHEDULE.bufferMinutes) || 0),
      ),
      startIntervalMinutes: Math.max(
        5,
        Math.round(
          Number(raw['startIntervalMinutes'] ?? DEFAULT_SCHEDULE.startIntervalMinutes) || 15,
        ),
      ),
      maxAppointmentsPerDay: Math.max(
        1,
        Math.round(
          Number(raw['maxAppointmentsPerDay'] ?? DEFAULT_SCHEDULE.maxAppointmentsPerDay) || 1,
        ),
      ),
    };
  }
}

function cloneDefaults(d: ScheduleDefaults): ScheduleDefaults {
  return {
    weekdayWindows: d.weekdayWindows.map((w) => ({ ...w })),
    weekendWindows: d.weekendWindows.map((w) => ({ ...w })),
    bufferMinutes: d.bufferMinutes,
    startIntervalMinutes: d.startIntervalMinutes,
    maxAppointmentsPerDay: d.maxAppointmentsPerDay,
  };
}
